import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  List,
  Space,
  Alert,
  Skeleton,
} from 'antd';
import DashboardLineChart from '../../../components/DashboardLineChart';
import DashboardBarChart from '../../../components/DashboardBarChart';
import {
  getRagDrift,
  getJudgeSummary,
  getRagTimeseries,
  getJudgeTimeseries,
  getRagDistribution,
} from '../../../services/api/ragMonitoring';
import { timeRangeToRagPeriod } from '../../../utils/timeRange';
import { useDashboard } from '../DashboardContext';
import DashboardStatCard from '../DashboardStatCard';
import DashboardAssistantFilter from '../DashboardAssistantFilter';
import { formatPercentageChange } from '../dashboardUtils';

const { Text } = Typography;

const STATUS_COLORS = { alert: 'error', warning: 'warning', ok: 'success' };

const RETRIEVAL_SERIES = [
  { key: 'mean_top_similarity', label: 'Mean top similarity', color: '#3b82f6', yAxisId: 'left' },
  { key: 'empty_retrieval_rate', label: 'Empty retrieval rate', color: '#ef4444', yAxisId: 'right' },
];

const JUDGE_SERIES = [
  { key: 'mean_faithfulness', label: 'Faithfulness', color: '#10b981' },
  { key: 'mean_relevance', label: 'Relevance', color: '#6366f1' },
];

function formatPctRate(v) {
  if (v == null) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

export default function RagQualitySection() {
  const { keycloak, timeRange, showAll, isAdmin } = useDashboard();
  const [searchParams] = useSearchParams();
  const assistantId = searchParams.get('assistantId');

  const [loading, setLoading] = useState(true);
  const [drift, setDrift] = useState(null);
  const [judge, setJudge] = useState(null);
  const [retrievalTs, setRetrievalTs] = useState([]);
  const [judgeTs, setJudgeTs] = useState([]);
  const [distribution, setDistribution] = useState(null);
  const [error, setError] = useState(null);

  const platformFlag = showAll && isAdmin;
  const period = timeRangeToRagPeriod(timeRange);

  const apiOpts = useMemo(() => ({
    period,
    timeRange,
    showAll: platformFlag,
    assistantId: assistantId ? Number(assistantId) : null,
  }), [period, timeRange, platformFlag, assistantId]);

  const loadData = useCallback(async () => {
    if (!keycloak?.authenticated) return;
    setLoading(true);
    setError(null);
    try {
      const [driftData, judgeData, retTs, judTs, dist] = await Promise.all([
        getRagDrift(keycloak, apiOpts),
        getJudgeSummary(keycloak, apiOpts),
        getRagTimeseries(keycloak, apiOpts),
        getJudgeTimeseries(keycloak, apiOpts),
        getRagDistribution(keycloak, apiOpts),
      ]);
      setDrift(driftData);
      setJudge(judgeData);
      setRetrievalTs(retTs);
      setJudgeTs(judTs);
      setDistribution(dist);
    } catch (e) {
      setError(e.message || 'Failed to load response quality data');
    } finally {
      setLoading(false);
    }
  }, [keycloak, apiOpts]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const judgeScoreData = useMemo(() => {
    if (!distribution) return [];
    return Object.entries(distribution.faithfulness_counts || {})
      .map(([score, count]) => ({ bin: score, count }))
      .filter((row) => row.count > 0);
  }, [distribution]);

  const relevanceScoreData = useMemo(() => {
    if (!distribution) return [];
    return Object.entries(distribution.relevance_counts || {})
      .map(([score, count]) => ({ bin: score, count }))
      .filter((row) => row.count > 0);
  }, [distribution]);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <DashboardAssistantFilter />

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} />}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <DashboardStatCard title="RAG health" loading={loading}>
            <Tag color={STATUS_COLORS[drift?.status] || 'default'} style={{ marginBottom: 8 }}>
              {(drift?.status || 'ok').toUpperCase()}
            </Tag>
            <Text type="secondary" className="dashboard-spend__meta">
              {drift?.total_traces ?? 0} traces
            </Text>
          </DashboardStatCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <DashboardStatCard title="Mean top similarity" loading={loading}>
            <div className="stat-value">
              {drift?.current?.mean_top_similarity != null
                ? drift.current.mean_top_similarity.toFixed(3)
                : '—'}
            </div>
            {formatPercentageChange(drift?.deltas?.mean_top_similarity, { invertColors: true })}
          </DashboardStatCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <DashboardStatCard title="Empty retrieval rate" loading={loading}>
            <div className="stat-value">
              {formatPctRate(drift?.current?.empty_retrieval_rate)}
            </div>
            {formatPercentageChange(drift?.deltas?.empty_retrieval_rate, { invertColors: true })}
          </DashboardStatCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <DashboardStatCard title="Judged responses" loading={loading}>
            <div className="stat-value">
              {drift?.judged_count ?? 0}
              <Text type="secondary" style={{ fontSize: 14, marginLeft: 4 }}>
                / {drift?.total_traces ?? 0}
              </Text>
            </div>
            {judge?.mean_faithfulness != null ? (
              <Text type="secondary" className="dashboard-spend__meta">
                Faith {judge.mean_faithfulness.toFixed(1)} · Rel {judge.mean_relevance?.toFixed(1) ?? '—'} / 5
              </Text>
            ) : null}
          </DashboardStatCard>
        </Col>
      </Row>

      <Card className="modern-card" title={<span className="card-title">Retrieval over time</span>}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <DashboardLineChart
            data={retrievalTs}
            series={RETRIEVAL_SERIES}
            dualAxis
            tooltipFormatter={(v, key) => (
              key === 'empty_retrieval_rate' ? formatPctRate(v) : Number(v).toFixed(3)
            )}
            emptyState={<Text type="secondary">No RAG traces in this period.</Text>}
          />
        )}
      </Card>

      <Card className="modern-card" title={<span className="card-title">Answer quality over time</span>}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <DashboardLineChart
            data={judgeTs}
            series={JUDGE_SERIES}
            tooltipFormatter={(v) => Number(v).toFixed(2)}
            emptyState={(
              <Text type="secondary">
                No judged responses in this period — scores appear when the LLM judge runs on traces.
              </Text>
            )}
          />
        )}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card className="modern-card" title={<span className="card-title">Similarity distribution</span>}>
            {loading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
              <>
                <DashboardBarChart
                  data={distribution?.similarity_histogram || []}
                  xKey="bin"
                  valueKey="count"
                />
                {distribution?.similarity_percentiles?.p50 != null && (
                  <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    P50: {distribution.similarity_percentiles.p50.toFixed(3)}
                    {' · '}
                    P95: {distribution.similarity_percentiles.p95?.toFixed(3) ?? '—'}
                  </Text>
                )}
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className="modern-card" title={<span className="card-title">Judge score breakdown</span>}>
            {loading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : judgeScoreData.length > 0 || relevanceScoreData.length > 0 ? (
              <Row gutter={16}>
                <Col span={12}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Faithfulness</Text>
                  <DashboardBarChart data={judgeScoreData} height={200} />
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Relevance</Text>
                  <DashboardBarChart data={relevanceScoreData} height={200} colors={['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe']} />
                </Col>
              </Row>
            ) : (
              <Text type="secondary">No judge scores in this period.</Text>
            )}
          </Card>
        </Col>
      </Row>

      {!loading && drift?.alerts?.length > 0 && (
        <Card className="modern-card" title={<span className="card-title">Alerts</span>}>
          <List
            size="small"
            dataSource={drift.alerts}
            renderItem={(item) => (
              <List.Item>
                <Tag color={item.severity === 'alert' ? 'red' : 'orange'}>{item.severity}</Tag>
                {item.message}
              </List.Item>
            )}
          />
        </Card>
      )}
    </Space>
  );
}
